import React, { useState, useEffect } from 'react';
import { Container, Button } from 'react-bootstrap';
import BingoForm from './components/BingoForm';
import BingoModal from './components/BingoModal';
import BingoProbability from './components/BingoProbability';
import BingoPreview from './components/BingoPreview';
import AdArea from './components/AdArea';
import { BingoCard, BINGO_THEMES, BingoTheme } from './types';
import { generateBingoCardsLocal } from './services/bingoService';
const logoColorido = '/assets/images/logo-colorido.svg';
const logoPB = '/assets/images/logo-pb.svg';

const convertSvgToPng = (svgUrl: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      console.log(`[DEBUG_LOG] convertSvgToPng - Image loaded: ${svgUrl}`, img.width, img.height);
      const canvas = document.createElement('canvas');
      // Set a minimum size or use natural dimensions
      const width = img.naturalWidth || img.width || 500;
      const height = img.naturalHeight || img.height || 500;
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Clear canvas with transparency
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/png');
        console.log(`[DEBUG_LOG] convertSvgToPng - Data URL generated for ${svgUrl}: ${dataUrl.substring(0, 50)}...`);
        resolve(dataUrl);
      } else {
        reject(new Error('Could not get canvas context'));
      }
    };
    img.onerror = () => reject(new Error(`Failed to load SVG: ${svgUrl}`));
    img.src = svgUrl;
  });
};

function App() {
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    const saved = localStorage.getItem('bingo_dark_mode');
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const [type, setType] = useState<'numbers' | 'names'>(() => (localStorage.getItem('bingo_type') as 'numbers' | 'names') || 'numbers');
  const [names, setNames] = useState<string>(() => localStorage.getItem('bingo_names') || '');
  const [usedNames, setUsedNames] = useState<string[]>([]);
  const [title, setTitle] = useState<string>(() => localStorage.getItem('bingo_title') || 'BINGO');
  const [count, setCount] = useState<number>(() => parseInt(localStorage.getItem('bingo_count') || '1'));
  const [image, setImage] = useState<string>('');
  const [theme, setTheme] = useState<BingoTheme>(() => {
    const savedThemeId = localStorage.getItem('bingo_theme_id');
    return BINGO_THEMES.find(t => t.id === savedThemeId) || BINGO_THEMES[0];
  });
  const [generatedCards, setGeneratedCards] = useState<BingoCard[] | null>(null);
  const [logoDataMap, setLogoDataMap] = useState<Record<string, string>>({});

  // Pre-load logos as PNG for PDF
  useEffect(() => {
    const loadLogos = async () => {
      try {
        const colorido = await convertSvgToPng(logoColorido);
        const pb = await convertSvgToPng(logoPB);
        setLogoDataMap({
          [logoColorido]: colorido,
          [logoPB]: pb
        });
        console.log("[DEBUG_LOG] App - Logos pre-loaded as PNG");
      } catch (error) {
        console.error("[DEBUG_LOG] App - Error pre-loading logos", error);
      }
    };
    loadLogos();
  }, []);

  // Sync with localStorage
  useEffect(() => {
    localStorage.setItem('bingo_type', type);
    localStorage.setItem('bingo_names', names);
    localStorage.setItem('bingo_title', title);
    localStorage.setItem('bingo_count', count.toString());
    localStorage.setItem('bingo_theme_id', theme.id);
    localStorage.setItem('bingo_dark_mode', JSON.stringify(darkMode));
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [type, names, title, count, theme, darkMode]);
  const [imagesMap, setImagesMap] = useState<Record<string, string>>({});
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleCreate = async () => {
    console.log("[DEBUG_LOG] App.handleCreate - Creating cards locally...");
    const namesList = type === 'names' ? names.split(',').map(n => n.trim()).filter(n => n !== '') : [];
    
    setShowModal(true);
    setIsLoading(true);
    try {
      setUsedNames(namesList);
      
      const response = await generateBingoCardsLocal({
        type,
        names: namesList,
        title,
        count,
        image
      });

      console.log("[DEBUG_LOG] App.handleCreate - Cards generated locally", response);
      setGeneratedCards(response.cards);
      
      const newImagesMap: Record<string, string> = {};
      response.images.forEach(img => {
        newImagesMap[img.id] = img.data;
      });
      setImagesMap(newImagesMap);
    } catch (error) {
      console.error("[DEBUG_LOG] App.handleCreate - Error generating cards", error);
      alert(error instanceof Error ? error.message : "Erro ao gerar cartelas");
      setShowModal(false);
    } finally {
      setIsLoading(false);
    }
  };

  const onHideModal = () => {
    console.log("[DEBUG_LOG] App.onHideModal - Hiding modal");
    setShowModal(false);
  };

  const handleReset = () => {
    console.log("[DEBUG_LOG] App.handleReset - Resetting form to default values");
    setType('numbers');
    setNames('');
    setTitle('BINGO');
    setCount(1);
    setImage('');
    setTheme(BINGO_THEMES[0]);
  };

  const isSubmitDisabled = (count <= 0) || (type === 'names' && names.split(',').map(n => n.trim()).filter(n => n !== '').length < 24);

  return (
    <div className="min-vh-100 animate-fade-in pb-5">
      <button
        className="dark-mode-toggle"
        onClick={() => setDarkMode(!darkMode)}
        title={darkMode ? 'Modo Claro' : 'Modo Escuro'}
      >
        {darkMode ? '☀️' : '🌙'}
      </button>
      <div className="bingo-header mb-5 text-center">
        <img 
          src={theme.id === 'default' ? logoPB : logoColorido} 
          alt="Grite Bingo Logo" 
          className="mb-4" 
          style={{ maxHeight: '180px', width: 'auto' }} 
        />
        <p className="lead mt-2 px-3" style={{ color: 'var(--bingo-text-muted)' }}>Crie suas próprias cartelas de forma simples e profissional!</p>
      </div>

      <Container className="pb-5">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="bingo-card-container mb-4">
              <BingoForm
                type={type}
                setType={setType}
                names={names}
                setNames={setNames}
                title={title}
                setTitle={setTitle}
                count={count}
                setCount={setCount}
                image={image}
                setImage={setImage}
                theme={theme}
                setTheme={setTheme}
                onSubmit={handleCreate}
                onReset={handleReset}
                isSubmitDisabled={isSubmitDisabled}
              />
            </div>

            <div className="row g-4 mb-5">
              <div className="col-lg-6">
                <div className="info-card h-100">
                  <BingoPreview
                    title={title}
                    type={type}
                    names={names}
                    image={image}
                    theme={theme}
                  />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="info-card h-100">
                  <BingoProbability count={count} names={names} type={type} />
                </div>
              </div>
            </div>

            <AdArea />

            <div className="d-flex gap-2 mt-4">
              <Button
                variant="secondary"
                className="flex-fill rounded-pill py-3"
                onClick={() => {
                  if (window.confirm("Tem certeza que deseja limpar todo o formulário?")) {
                    console.log("[DEBUG_LOG] App.handleReset - Clearing entire form via button");
                    handleReset();
                  }
                }}
              >
                Limpar
              </Button>
              <Button
                variant="primary"
                className="btn-bingo flex-fill rounded-pill py-3"
                disabled={isSubmitDisabled}
                onClick={handleCreate}
                style={{ boxShadow: 'none' }}
              >
                GERAR MINHAS CARTELAS!
              </Button>
            </div>

            {isSubmitDisabled && (
              <div className="text-center mt-2">
                <small className="text-danger fw-bold">
                  {count <= 0 ? "A quantidade de cartelas deve ser maior que zero." : "Adicione pelo menos 24 nomes para continuar."}
                </small>
              </div>
            )}
          </div>
        </div>

        <BingoModal
          show={showModal}
          onHide={onHideModal}
          isLoading={isLoading}
          generatedCards={generatedCards}
          imagesMap={imagesMap}
          logoDataMap={logoDataMap}
          type={type}
          names={usedNames}
          theme={theme}
        />
      </Container>
    </div>
  );
}

export default App;
